import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';

import { useBranding } from '../../context/BrandingContext';
import { brandingApi, uploadsApi, authApi, sipConfigApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  User,
  Sun,
  Moon,
  Bell,
  Shield,
  Palette,
  Save,
  Info,
  Lock,
  Eye,
  EyeOff,
  Check,
  X,
  Phone,
  Globe,
  PhoneOutgoing,
} from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();
  const { theme, setTheme, isDark } = useTheme();
  const { branding, refresh: refreshBranding } = useBranding();
  const [brandName, setBrandName] = useState(branding?.app_name || 'SpillConsult EMS');
  const [brandLogoFile, setBrandLogoFile] = useState(null);
  const [brandingSaving, setBrandingSaving] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordChanging, setPasswordChanging] = useState(false);

  // SIP Config state
  const [sipConfig, setSipConfig] = useState({
    enabled: false, wss_url: '', sip_domain: '', sip_username: '',
    sip_password: '', display_name: '', voipfone_trunk_user: '',
    voipfone_trunk_pass: '', stun_server: 'stun:stun.l.google.com:19302', notes: ''
  });
  const [sipSaving, setSipSaving] = useState(false);
  const [sipTesting, setSipTesting] = useState(false);
  const isAdmin = user?.roles?.includes('super_admin');

  useEffect(() => {
    if (isAdmin) {
      sipConfigApi.getConfig().then(c => { if (c) setSipConfig(c); }).catch(() => {});
    }
  }, [isAdmin]);

  // Password strength check
  const passwordChecks = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    match: newPassword && confirmPassword && newPassword === confirmPassword,
  };

  const passwordStrength = Object.values(passwordChecks).filter(Boolean).length;
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    setPasswordChanging(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setPasswordChanging(false);
    }
  };

  React.useEffect(() => {
    setBrandName(branding?.app_name || 'SpillConsult EMS');
  }, [branding?.app_name]);

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    if (!user?.roles?.includes('super_admin')) {
      toast.error('Only Super Admin can update branding');
      return;
    }

    setBrandingSaving(true);
    try {
      let logoFileId = branding?.logo_file_id || null;
      if (brandLogoFile) {
        const uploaded = await uploadsApi.upload(brandLogoFile);
        logoFileId = uploaded.file_id;
      }

      await brandingApi.update({
        app_name: brandName,
        logo_file_id: logoFileId,
      });

      await refreshBranding();
      setBrandLogoFile(null);
      toast.success('Branding updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update branding');
    } finally {
      setBrandingSaving(false);
    }
  };

  
  // Profile form state
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  
  // Notification preferences
  const [notifExercise, setNotifExercise] = useState(true);
  const [notifInject, setNotifInject] = useState(true);
  const [notifReminder, setNotifReminder] = useState(true);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // This would normally call an API to update the user profile
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'super_admin': return 'destructive';
      case 'simcel': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <DashboardLayout title="Settings">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Profile Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="font-secondary">Profile</CardTitle>
                <CardDescription>Manage your account information</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
                  ) : (
                    <span className="font-secondary font-bold text-2xl text-primary">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <div className="flex gap-1 mt-1">
                    {user?.roles?.map((role) => (
                      <Badge 
                        key={role} 
                        variant={getRoleBadgeVariant(role)}
                        className="capitalize text-xs"
                      >
                        {{ hub_admin: 'Hub Admin', super_admin: 'Super Admin', simcel: 'Simcell', observer: 'Observer', participant: 'Participant' }[role] || role.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label htmlFor="name">Display Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="profile-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed
                </p>
              </div>
              
              <Button type="submit" disabled={saving} data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Palette className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="font-secondary">Appearance</CardTitle>
                <CardDescription>Customize the look and feel</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                <div>
                  <p className="font-medium">Theme</p>
                  <p className="text-sm text-muted-foreground">
                    {isDark ? 'Dark mode is enabled' : 'Light mode is enabled'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={!isDark ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTheme('light')}
                  data-testid="light-theme-btn"
                >
                  <Sun className="w-4 h-4 mr-1" />
                  Light
                </Button>
                <Button
                  variant={isDark ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTheme('dark')}
                  data-testid="dark-theme-btn"
                >
                  <Moon className="w-4 h-4 mr-1" />
                  Dark
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Branding (Super Admin) */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Palette className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="font-secondary">Branding</CardTitle>
                <CardDescription>Logo and application name (Super Admin only)</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!user?.roles?.includes('super_admin') ? (
              <p className="text-sm text-muted-foreground">Only Super Admin can update branding.</p>
            ) : (
              <form onSubmit={handleSaveBranding} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="brand-name">Application Name</Label>
                  <Input
                    id="brand-name"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="SpillConsult EMS"
                    data-testid="branding-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="brand-logo">Logo</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground" aria-label="Branding help">
                            <Info className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[320px]">
                          <div className="space-y-1">
                            <p className="font-medium">Logo guidance</p>
                            <p>Recommended: square image (e.g. 512×512), PNG/SVG. The logo will auto-fit in the UI.</p>
                            <p>To change later: upload a new logo and click “Save Branding”.</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input
                    id="brand-logo"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setBrandLogoFile((e.target.files || [])[0] || null)}
                    data-testid="branding-logo-input"
                  />

                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl border bg-card overflow-hidden flex items-center justify-center">
                      {branding?.logo_file_id ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${branding.logo_file_id}?token=${encodeURIComponent(localStorage.getItem('ems_token') || '')}`}
                          alt=""
                          className="w-full h-full object-contain p-1"
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">No logo</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Upload a square logo (recommended).
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={brandingSaving} data-testid="branding-save-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {brandingSaving ? 'Saving...' : 'Save Branding'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>


        {/* Notifications */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="font-secondary">Notifications</CardTitle>
                <CardDescription>Configure notification preferences</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Exercise Notifications</p>
                <p className="text-sm text-muted-foreground">
                  Get notified when you&apos;re assigned to exercises
                </p>
              </div>
              <Switch
                checked={notifExercise}
                onCheckedChange={setNotifExercise}
                data-testid="notif-exercise-switch"
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Inject Alerts</p>
                <p className="text-sm text-muted-foreground">
                  Receive alerts when injects are triggered
                </p>
              </div>
              <Switch
                checked={notifInject}
                onCheckedChange={setNotifInject}
                data-testid="notif-inject-switch"
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Reminders</p>
                <p className="text-sm text-muted-foreground">
                  Get reminders for upcoming exercises
                </p>
              </div>
              <Switch
                checked={notifReminder}
                onCheckedChange={setNotifReminder}
                data-testid="notif-reminder-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Security Info */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="font-secondary">Security</CardTitle>
                <CardDescription>Account security information</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">User ID</p>
                <p className="text-sm text-muted-foreground font-mono">
                  {user?.user_id}
                </p>
              </div>
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Account Created</p>
                <p className="text-sm text-muted-foreground">
                  {user?.created_at && new Date(user.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Organisation</p>
                <p className="text-sm text-muted-foreground font-mono">
                  {user?.organisation_id || 'Default Organisation'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="font-secondary">Change Password</CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              {/* Current Password */}
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {/* Password Strength */}
                {newPassword && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Progress value={(passwordStrength / 6) * 100} className="h-2 flex-1" />
                      <span className="text-xs text-muted-foreground">
                        {passwordStrength}/6
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className={`flex items-center gap-1 ${passwordChecks.length ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {passwordChecks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        At least 8 characters
                      </div>
                      <div className={`flex items-center gap-1 ${passwordChecks.uppercase ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {passwordChecks.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        Uppercase letter
                      </div>
                      <div className={`flex items-center gap-1 ${passwordChecks.lowercase ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {passwordChecks.lowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        Lowercase letter
                      </div>
                      <div className={`flex items-center gap-1 ${passwordChecks.number ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {passwordChecks.number ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        Number
                      </div>
                      <div className={`flex items-center gap-1 ${passwordChecks.special ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {passwordChecks.special ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        Special character (!@#$%^&*)
                      </div>
                      <div className={`flex items-center gap-1 ${passwordChecks.match ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {passwordChecks.match ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        Passwords match
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <Button 
                type="submit" 
                disabled={passwordChanging || !isPasswordValid}
                data-testid="change-password-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                {passwordChanging ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* SIP / External Calling Configuration (Admin only) */}
        {isAdmin && (
          <Card data-testid="sip-config-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Advanced External Calling Configuration
              </CardTitle>
              <CardDescription>
                Configure SIP connection for external phone calls via FreeSWITCH, Telnyx, or custom SIP provider
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Enable toggle + Provider selector */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <Label>Enable External Calling</Label>
                    <p className="text-xs text-muted-foreground">Activate SIP-based external calls</p>
                  </div>
                </div>
                <Switch
                  checked={sipConfig.enabled}
                  onCheckedChange={(v) => setSipConfig(prev => ({ ...prev, enabled: v }))}
                  data-testid="sip-enabled-switch"
                />
              </div>

              <Separator />

              {/* Provider Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Connection Provider</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { id: 'freeswitch', name: 'FreeSWITCH', desc: 'Self-hosted PBX on your server (AWS/VPS)', icon: '🖥️' },
                    { id: 'telnyx', name: 'Telnyx', desc: 'Cloud SIP provider with WebRTC SDK', icon: '☁️' },
                    { id: 'custom', name: 'Custom SIP', desc: 'Any SIP server with WSS support', icon: '⚙️' },
                  ].map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSipConfig(prev => ({ ...prev, provider: p.id }))}
                      className={`text-left p-3 rounded-lg border-2 transition-all ${
                        (sipConfig.provider || 'freeswitch') === p.id
                          ? 'border-primary bg-primary/5'
                          : 'border-muted hover:border-muted-foreground/30'
                      }`}
                      data-testid={`sip-provider-${p.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{p.icon}</span>
                        <span className="font-medium text-sm">{p.name}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">{p.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <Separator />

              {/* SIP Connection Settings */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Phone className="w-4 h-4" /> SIP Connection
                </Label>

                {/* WSS URL - shown for FreeSWITCH and Custom (not Telnyx) */}
                {(sipConfig.provider || 'freeswitch') !== 'telnyx' && (
                  <div className="space-y-2">
                    <Label>WebSocket URL (WSS)</Label>
                    <Input
                      placeholder={
                        (sipConfig.provider || 'freeswitch') === 'freeswitch'
                          ? 'wss://YOUR-EC2-IP:7443'
                          : 'wss://sip.provider.com:443'
                      }
                      value={sipConfig.wss_url}
                      onChange={e => setSipConfig(prev => ({ ...prev, wss_url: e.target.value }))}
                      data-testid="sip-wss-url"
                      className={sipConfig.wss_url && !sipConfig.wss_url.startsWith('wss://') && !sipConfig.wss_url.startsWith('ws://') ? 'border-red-400' : ''}
                    />
                    {sipConfig.wss_url && !sipConfig.wss_url.startsWith('wss://') && !sipConfig.wss_url.startsWith('ws://') ? (
                      <p className="text-[11px] text-red-500">Must start with wss:// or ws://</p>
                    ) : (sipConfig.provider || 'freeswitch') === 'freeswitch' ? (
                      <p className="text-[11px] text-muted-foreground">Your FreeSWITCH server WebSocket endpoint (port 7443)</p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">SIP provider WebSocket endpoint</p>
                    )}
                  </div>
                )}

                {(sipConfig.provider || 'freeswitch') === 'telnyx' && (
                  <div className="p-2.5 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                    Telnyx WebRTC SDK handles connection automatically — no WSS URL needed
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(sipConfig.provider || 'freeswitch') !== 'telnyx' && (
                    <div className="space-y-2">
                      <Label>SIP Domain</Label>
                      <Input
                        placeholder={(sipConfig.provider || 'freeswitch') === 'freeswitch' ? 'YOUR-EC2-IP' : 'sip.provider.com'}
                        value={sipConfig.sip_domain}
                        onChange={e => setSipConfig(prev => ({ ...prev, sip_domain: e.target.value }))}
                        data-testid="sip-domain"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        {(sipConfig.provider || 'freeswitch') === 'freeswitch' ? 'Same as your server IP or domain' : 'SIP server domain'}
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>SIP Username / Extension</Label>
                    <Input
                      placeholder={(sipConfig.provider || 'freeswitch') === 'freeswitch' ? '1001' : 'username'}
                      value={sipConfig.sip_username}
                      onChange={e => setSipConfig(prev => ({ ...prev, sip_username: e.target.value }))}
                      data-testid="sip-username"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {(sipConfig.provider || 'freeswitch') === 'freeswitch'
                        ? 'FreeSWITCH extension number (e.g., 1001)'
                        : (sipConfig.provider || 'freeswitch') === 'telnyx'
                        ? 'Telnyx credential connection username'
                        : 'SIP account username'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>SIP Password</Label>
                    <Input
                      type="password"
                      placeholder="SIP password"
                      value={sipConfig.sip_password}
                      onChange={e => setSipConfig(prev => ({ ...prev, sip_password: e.target.value }))}
                      data-testid="sip-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Display Name (Caller ID Name)</Label>
                    <Input
                      placeholder="EMS Control Room"
                      value={sipConfig.display_name}
                      onChange={e => setSipConfig(prev => ({ ...prev, display_name: e.target.value }))}
                      data-testid="sip-display-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Outbound Caller ID Number</Label>
                    <Input
                      placeholder="+441234567890"
                      value={sipConfig.caller_id || ''}
                      onChange={e => setSipConfig(prev => ({ ...prev, caller_id: e.target.value }))}
                      data-testid="sip-caller-id"
                    />
                    <p className="text-[11px] text-muted-foreground">Phone number shown to call recipients</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* NAT / STUN / TURN */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Shield className="w-4 h-4" /> NAT Traversal (STUN / TURN)
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>STUN Server</Label>
                    <Input
                      placeholder="stun:stun.l.google.com:19302"
                      value={sipConfig.stun_server}
                      onChange={e => setSipConfig(prev => ({ ...prev, stun_server: e.target.value }))}
                      data-testid="sip-stun"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>TURN Server URL</Label>
                    <Input
                      placeholder="turn:turn.example.com:3478"
                      value={sipConfig.turn_url || ''}
                      onChange={e => setSipConfig(prev => ({ ...prev, turn_url: e.target.value }))}
                      data-testid="sip-turn-url"
                    />
                    <p className="text-[11px] text-muted-foreground">Required for strict firewalls</p>
                  </div>
                  <div className="space-y-2">
                    <Label>TURN Username</Label>
                    <Input
                      placeholder="turn-user"
                      value={sipConfig.turn_username || ''}
                      onChange={e => setSipConfig(prev => ({ ...prev, turn_username: e.target.value }))}
                      data-testid="sip-turn-user"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>TURN Password</Label>
                    <Input
                      type="password"
                      placeholder="turn-password"
                      value={sipConfig.turn_password || ''}
                      onChange={e => setSipConfig(prev => ({ ...prev, turn_password: e.target.value }))}
                      data-testid="sip-turn-pass"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Voipfone SIP Trunk */}
              {(sipConfig.provider || 'freeswitch') === 'freeswitch' && (
                <>
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      <PhoneOutgoing className="w-4 h-4" /> Voipfone SIP Trunk
                    </Label>
                    <p className="text-xs text-muted-foreground -mt-1">
                      Connect FreeSWITCH to Voipfone for external PSTN calls (UK landlines & mobiles)
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Voipfone Account / Username</Label>
                        <Input
                          placeholder="30159481"
                          value={sipConfig.voipfone_trunk_user}
                          onChange={e => setSipConfig(prev => ({ ...prev, voipfone_trunk_user: e.target.value }))}
                          data-testid="sip-voipfone-user"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Voipfone Password</Label>
                        <Input
                          type="password"
                          placeholder="Trunk password"
                          value={sipConfig.voipfone_trunk_pass}
                          onChange={e => setSipConfig(prev => ({ ...prev, voipfone_trunk_pass: e.target.value }))}
                          data-testid="sip-voipfone-pass"
                        />
                      </div>
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Configuration Notes</Label>
                <Input
                  placeholder="e.g., FreeSWITCH on AWS eu-west-2, connected to Voipfone..."
                  value={sipConfig.notes || ''}
                  onChange={e => setSipConfig(prev => ({ ...prev, notes: e.target.value }))}
                  data-testid="sip-notes"
                />
              </div>

              {/* Quick Reference */}
              {(sipConfig.provider || 'freeswitch') === 'freeswitch' && (
                <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
                  <p className="text-xs font-semibold">FreeSWITCH Quick Reference</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground">
                    <span>Default WSS Port:</span><span className="font-mono">7443</span>
                    <span>Default WS Port:</span><span className="font-mono">5066</span>
                    <span>SIP Port:</span><span className="font-mono">5060</span>
                    <span>RTP Ports:</span><span className="font-mono">16384-32768</span>
                    <span>Config Dir:</span><span className="font-mono">/etc/freeswitch/</span>
                    <span>Reload:</span><span className="font-mono">fs_cli -x &quot;reloadxml&quot;</span>
                  </div>
                </div>
              )}

              {(sipConfig.provider || 'freeswitch') === 'telnyx' && (
                <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
                  <p className="text-xs font-semibold">Telnyx Quick Reference</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground">
                    <span>Portal:</span><span className="font-mono">portal.telnyx.com</span>
                    <span>Credentials:</span><span>SIP &gt; Connections</span>
                    <span>Numbers:</span><span>Numbers &gt; My Numbers</span>
                    <span>Voice Apps:</span><span>Voice &gt; Applications</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={async () => {
                    setSipSaving(true);
                    try {
                      await sipConfigApi.updateConfig(sipConfig);
                      toast.success('SIP configuration saved');
                    } catch {
                      toast.error('Failed to save SIP configuration');
                    } finally {
                      setSipSaving(false);
                    }
                  }}
                  disabled={sipSaving}
                  data-testid="sip-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {sipSaving ? 'Saving...' : 'Save Configuration'}
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    setSipTesting(true);
                    try {
                      const result = await sipConfigApi.testConnection();
                      if (result.status === 'ok') {
                        toast.success(result.message);
                      } else {
                        toast.error(result.message);
                      }
                    } catch {
                      toast.error('Connection test failed');
                    } finally {
                      setSipTesting(false);
                    }
                  }}
                  disabled={sipTesting}
                  data-testid="sip-test-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {sipTesting ? 'Testing...' : 'Test Connection'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
